import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchSites } from 'store/sitesSlice';
import { fetchProviders } from 'store/paymentProvidersSlice';
import { fetchDepositTransactions, initialParams } from 'store/transactionsSlice';
import { RootState } from 'store/index';
import { ROWS_PER_PAGE_OPTIONS } from 'constants/pagination';
import { POLLING_INTERVAL } from 'constants/config';
import { commonGridStyles } from 'styles/gridStyles';
import commonStyles from 'styles/common.module.scss';
import { formatDate } from 'utils/date';
import styles from './Deposit.module.scss';

import dayjs, { Dayjs } from 'dayjs';
import RangeDatePicker from 'components/common/RangeDatePicker/RangeDatePicker';
import CustomPagination from 'components/common/pagination/pagination';
import CopyableId from 'components/common/CopyableId/CopyableId';
import ConfirmModal from 'components/modals/ConfirmModal';
import { transactionService } from 'components/api/transactions';
import { showToast } from 'store/toastSlice';
import { showLoader, hideLoader } from 'store/uiSlice';

const STATUSES = ['Created', 'Completed', 'Pending', 'Cancelled', 'Frozen'];

const Deposit: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: sites, isLoading: sitesLoading } = useAppSelector(
    (state: RootState) => state.sites,
  );
  const { data: providers, isLoading: providersLoading } = useAppSelector(
    (state: RootState) => state.paymentProviders,
  );
  const { data, total, isLoading } = useAppSelector((state) => state.transactions.deposit);

  const [params, setParams] = useState({ ...initialParams, status: 'all_non_pending' });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  const [modal, setModal] = useState<{
    open: boolean;
    type: 'confirm' | 'cancel' | null;
    row: any;
  }>({
    open: false,
    type: null,
    row: null,
  });

  const fromDate = params.dateFrom ? dayjs(params.dateFrom) : null;
  const toDate = params.dateTo ? dayjs(params.dateTo) : null;

  useEffect(() => {
    if (sites.length === 0) {
      dispatch(fetchSites({ page: 1, limit: 100, sortField: 'id', sortValue: 'ASC' }));
    }
  }, [dispatch, sites.length]);

  useEffect(() => {
    if (providers.length === 0) {
      dispatch(fetchProviders());
    }
  }, [dispatch, providers.length]);

  useEffect(() => {
    if (sites.length > 0 && isInitialLoad) {
      const defaultSite = sites.find((s) => s.title.toLowerCase().includes('dgenta')) || sites[0];
      if (defaultSite) {
        setParams((prev) => ({ ...prev, siteId: defaultSite.id }));
        setIsInitialLoad(false);
      }
    }
  }, [sites, isInitialLoad]);

  const loadData = useCallback(() => {
    if (!isInitialLoad && params.siteId !== 'all') {
      dispatch(fetchDepositTransactions(params));
    }
  }, [dispatch, params, isInitialLoad]);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      if (!isInitialLoad && params.siteId !== 'all') {
        dispatch(fetchDepositTransactions({ ...params, isBackground: true }));
      }
    }, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [loadData, isInitialLoad, params, dispatch]);

  const handleSiteChange = (siteId: string | number) => {
    setParams((prev) => ({ ...prev, siteId, page: 1 }));
  };

  const handleStatusChange = (status: string) => {
    setParams((prev) => ({ ...prev, status, page: 1 }));
  };

  const handleProviderChange = (providerId: string | number) => {
    setParams((prev) => ({ ...prev, providerId, page: 1 }));
  };

  const handleDateChange = (from: Dayjs | null, to: Dayjs | null) => {
    setParams((prev) => ({
      ...prev,
      dateFrom: from ? from.toISOString() : null,
      dateTo: to ? to.toISOString() : null,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setParams((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (limit: number) => {
    setParams((prev) => ({ ...prev, limit, page: 1 }));
  };

  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      setParams((prev) => ({
        ...prev,
        sortField: model[0].field,
        sortValue: model[0].sort?.toUpperCase() as 'ASC' | 'DESC',
      }));
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setParams((prev) => ({
      ...prev,
      page: 1,
      status: newValue === 1 ? 'pending' : newValue === 2 ? 'frozen' : 'all_non_pending',
    }));
  };

  const handleModalAction = async () => {
    if (!modal.row || !modal.type) return;

    dispatch(showLoader({ text: modal.type === 'confirm' ? 'Confirming...' : 'Cancelling...' }));
    try {
      if (modal.type === 'confirm') {
        const rowAmount = modal.row.amountUsd || modal.row.amount;
        const confirmNote =
          modal.row.failReason || 'Verified against AlphaPo transactions/info — real deposit.';
        await transactionService.confirmDepositTransaction(
          modal.row.id,
          String(rowAmount),
          confirmNote,
        );
        dispatch(showToast({ text: 'Deposit confirmed successfully', type: 'success' }));
      } else {
        const cancelNote =
          modal.row.failReason ||
          `Confirmed with AlphaPo support (ticket #${modal.row.id}) — no such payout, safe to close.`;
        await transactionService.cancelDepositTransaction(modal.row.id, cancelNote);
        dispatch(showToast({ text: 'Deposit cancelled successfully', type: 'success' }));
      }
      loadData();
    } catch (error: any) {
      dispatch(
        showToast({
          text: error.response?.data?.message || `Failed to ${modal.type} deposit`,
          type: 'error',
        }),
      );
    } finally {
      dispatch(hideLoader());
      setModal({ open: false, type: null, row: null });
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'externalTxId',
      headerName: 'External Tx Id',
      width: 300,
      renderCell: (params) => <CopyableId value={params.value} label="external transaction id" />,
    },
    {
      field: 'user',
      headerName: 'User',
      width: 150,
      renderCell: (params) => (
        <span
          className={styles.userLink}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/clients/${params.row.user?.id || params.row.userId}`);
          }}
        >
          {params.row.user?.username || params.value || '-'}
        </span>
      ),
    },
    {
      field: 'cryptoName',
      headerName: 'Method',
      width: 200,
      renderCell: (params) => (
        <>
          {params.row?.methodImageUrl && (
            <img
              style={{ marginRight: '6px' }}
              width={20}
              src={params.row.methodImageUrl}
              alt={params.row.cryptoName}
            />
          )}
          <Typography fontSize={12}>
            {params.row.cryptoName || ''}
            {params.row.admin?.username ? ` (${params.row.admin.username})` : ''}
          </Typography>
        </>
      ),
    },
    {
      field: 'provider',
      headerName: 'Provider name',
      width: 200,
      renderCell: (params) => params.row?.provider?.name || '-',
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 150,
      renderCell: (params) => (
        <Typography className={styles.amount}>
          {' '}
          $ {Number(params.value).toLocaleString()}{' '}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => (
        <span className={`${styles.status} ${styles[params.value?.toLowerCase() as string]}`}>
          {params.value ? params.value.charAt(0).toUpperCase() + params.value.slice(1) : '-'}
        </span>
      ),
    },
    {
      field: 'site',
      headerName: 'Site',
      width: 120,
      renderCell: (params) => params.row.site?.name || '-',
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      width: 200,
      renderCell: (params) => formatDate(params.value || params.row.date),
    },
  ];

  if (activeTab === 2) {
    columns.push({
      field: 'failReason',
      headerName: 'Operator Note',
      width: 220,
      renderCell: (params) => (
        <Typography fontSize={12} sx={{ color: 'var(--text-secondary)' }}>
          {params.value || '-'}
        </Typography>
      ),
    });
  }

  if (activeTab === 1 || activeTab === 2) {
    columns.push({
      field: 'actions',
      headerName: 'Actions',
      width: activeTab === 2 ? 120 : 200,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center" height="100%">
          <Button
            size="small"
            variant="contained"
            sx={{
              backgroundColor: '#2a8039',
              '&:hover': { backgroundColor: '#246f30' },
              textTransform: 'none',
              fontSize: '12px',
            }}
            onClick={() => {
              setModal({
                open: true,
                type: 'confirm',
                row: params.row,
              });
            }}
          >
            Confirm
          </Button>
          {activeTab === 1 && (
            <Button
              size="small"
              variant="contained"
              sx={{
                backgroundColor: '#ff4d4f',
                '&:hover': { backgroundColor: '#d9363e' },
                textTransform: 'none',
                fontSize: '12px',
              }}
              onClick={() => {
                setModal({
                  open: true,
                  type: 'cancel',
                  row: params.row,
                });
              }}
            >
              Cancel
            </Button>
          )}
        </Stack>
      ),
    });
  }

  const renderPagination = () => (
    <CustomPagination
      count={total || 0}
      page={params.page - 1}
      onPageChange={(_, newPage) => handlePageChange(newPage + 1)}
      rowsPerPage={params.limit}
      onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
    />
  );

  return (
    <Box className={styles.depositContainer}>
      <Box className={styles.header} sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" className={styles.title}>
            Deposit History
          </Typography>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': { color: 'var(--text-secondary)' },
              '& .Mui-selected': { color: 'var(--accent-blue) !important' },
              '& .MuiTabs-indicator': { backgroundColor: 'var(--accent-blue)' },
            }}
          >
            <Tab label="Deposits" />
            <Tab label="Pending Deposits" />
            <Tab label="Frozen Deposits" />
          </Tabs>
        </Box>

        <Box className={styles.controls}>
          <Box className={styles.filterItem}>
            <RangeDatePicker
              fromDate={fromDate}
              toDate={toDate}
              onFromChange={(val) => handleDateChange(val, toDate)}
              onToChange={(val) => handleDateChange(fromDate, val)}
            />
          </Box>
          <Box className={styles.filterItem}>
            <FormControl size="small" className={styles.selectContainer}>
              <InputLabel sx={{ color: '#8698a6' }}>Site</InputLabel>
              <Select
                value={params.siteId === 'all' ? '' : params.siteId}
                label="Site"
                onChange={(e) => handleSiteChange(e.target.value as string | number)}
                disabled={sitesLoading}
              >
                {sites.map((site) => (
                  <MenuItem key={site.id} value={site.id}>
                    {site.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box className={styles.filterItem}>
            <FormControl size="small" className={styles.selectContainer}>
              <InputLabel sx={{ color: '#8698a6' }}>Status</InputLabel>
              <Select
                value={
                  params.status ||
                  (activeTab === 1 ? 'pending' : activeTab === 2 ? 'frozen' : 'all_non_pending')
                }
                label="Status"
                onChange={(e) => handleStatusChange(e.target.value.toLowerCase())}
                disabled={activeTab !== 0}
              >
                {activeTab === 0 ? (
                  <>
                    <MenuItem value="all_non_pending">All Statuses</MenuItem>
                    {STATUSES.filter((s) => s !== 'Pending' && s !== 'Frozen').map((status) => (
                      <MenuItem key={status} value={status.toLowerCase()}>
                        {status}
                      </MenuItem>
                    ))}
                  </>
                ) : activeTab === 1 ? (
                  <MenuItem value="pending">Pending</MenuItem>
                ) : (
                  <MenuItem value="frozen">Frozen</MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>

          <Box className={styles.filterItem}>
            <FormControl size="small" className={styles.selectContainer}>
              <InputLabel sx={{ color: '#8698a6' }}>Provider</InputLabel>
              <Select
                value={params.providerId || 'all'}
                label="Provider"
                onChange={(e) => handleProviderChange(e.target.value)}
                disabled={providersLoading}
              >
                <MenuItem value="all">All Providers</MenuItem>
                {providers.map((provider) => (
                  <MenuItem key={provider.code} value={provider.id}>
                    {provider.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      <Box className={commonStyles.gridWrapper}>
        {renderPagination()}
        <DataGrid
          rows={data}
          columns={columns}
          loading={isLoading}
          rowCount={total}
          onSortModelChange={handleSortModelChange}
          sx={commonGridStyles}
        />
        {renderPagination()}
      </Box>

      <ConfirmModal
        open={modal.open}
        title={modal.type === 'confirm' ? 'Confirm Deposit' : 'Cancel Deposit'}
        onClose={() => setModal({ ...modal, open: false })}
        onConfirm={handleModalAction}
        confirmText={modal.type === 'confirm' ? 'Confirm' : 'Cancel Deposit'}
        isDanger={modal.type === 'cancel'}
      >
        <Typography sx={{ mb: modal.row?.failReason ? 1.5 : 3 }}>
          Are you sure you want to {modal.type} the deposit for user{' '}
          <strong style={{ color: 'var(--accent-blue)' }}>
            {modal.row?.user?.username || 'user'}
          </strong>
          ?
        </Typography>
        {modal.row?.failReason && (
          <Typography variant="body2" sx={{ mb: 3, color: 'var(--text-secondary)' }}>
            <strong>Note:</strong> {modal.row.failReason}
          </Typography>
        )}
      </ConfirmModal>
    </Box>
  );
};

export default Deposit;
